import { forwardRef, useImperativeHandle, useRef } from 'react';
import { CodeEditor } from '../CodeEditor/CodeEditor';
import type { CodeEditorRef } from '../CodeEditor/CodeEditor.types';
import { FormField } from '../FormField/FormField';
import type { CodeEditorFieldProps } from './CodeEditorField.types';

export const CodeEditorField = forwardRef<CodeEditorRef, CodeEditorFieldProps>(
  (
    {
      className,
      description,
      disabled,
      error,
      id,
      label,
      labelTooltip,
      inputRef,
      link,
      primaryHelperText,
      secondaryHelperText,
      size = 'default',
      hasError,
      ...editorProps
    },
    ref,
  ) => {
    const editorRef = useRef<CodeEditorRef>(null);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editorRef.current?.focus(),
        blur: () => editorRef.current?.blur(),
      }),
      [],
    );

    const showError = error !== undefined && error !== '';

    return (
      <FormField
        className={className}
        description={description}
        disabled={disabled}
        error={error}
        id={id}
        label={label}
        labelTooltip={labelTooltip}
        link={link}
        primaryHelperText={primaryHelperText}
        secondaryHelperText={secondaryHelperText}
        size={size}
        // <label htmlFor> does not auto-focus a contenteditable, so wire an
        // explicit click handler that calls the editor's imperative focus().
        onLabelClick={() => editorRef.current?.focus()}
        renderControl={({
          baseId,
          labelId,
          descriptionId,
          helperId,
          errorId,
        }) => {
          const ariaDescribedBy =
            [descriptionId, helperId, errorId].filter(Boolean).join(' ') ||
            undefined;
          return (
            <CodeEditor
              ref={editorRef}
              {...editorProps}
              inputRef={inputRef}
              id={baseId}
              size={size}
              disabled={disabled}
              hasError={hasError || showError}
              aria-labelledby={labelId}
              aria-describedby={ariaDescribedBy}
            />
          );
        }}
      />
    );
  },
);
